import type { Asset } from '@passkeys/assets';
import { getChainDisplayName } from '@passkeys/assets';
import Skeleton from '@passkeys/ui/skeleton';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useParams } from 'wouter';

import Subpage from '@/ui/components/subpage';
import useAsset from '@/ui/hooks/use-asset';
import useBalance from '@/ui/hooks/use-balance';
import useRates from '@/ui/hooks/use-rates';
import useTrackAssetView from '@/ui/pages/token-detail/hooks/use-track-asset-view';
import Actions from './components/actions.js';
import Activity from './components/activity.js';
import Hero from './components/hero.js';
import useActivity from '../../hooks/use-activity.js';

function getTitleForAsset(asset: Asset) {
  if (asset.baseAssetName === asset.name) {
    return `${asset.displayTicker} (${getChainDisplayName(asset)})`;
  }

  return `${asset.displayTicker} (${asset.displayName})`;
}

function TokenDetailPage() {
  const { token } = useParams<{ token: string }>();

  // TODO: Render 404 page.
  invariant(token, "TokenDetailPage: Missing 'token' param");

  // TODO: Decide where to trace boundary between legacy terminology ("asset")
  // and new terminology ("token").
  const assetName = token;

  const asset = useAsset(assetName);
  useTrackAssetView(asset);

  const { value: balance, loading: isBalanceLoading } = useBalance(assetName);

  const { value: rates, loading: isRatesLoading } = useRates();
  const assetRate = useMemo(
    () => (asset ? rates[asset.ticker] : undefined),
    [asset, rates],
  );

  const activity = useActivity({ assetNames: [assetName] });

  const loading = !asset || isBalanceLoading || isRatesLoading;
  const title = asset ? getTitleForAsset(asset) : '';

  return (
    <Subpage
      title={title}
      className="flex h-full flex-col items-center overflow-hidden"
    >
      <Hero
        asset={asset}
        balance={balance?.toDefaultNumber()}
        price={assetRate?.price}
        priceChangePercentage={assetRate ? assetRate.change24 / 100 : undefined}
        priceUnavailable={assetRate?.invalid ?? false}
      />

      <div className="flex w-full flex-col items-center py-6">
        {loading ? (
          <Skeleton width={242} height={56} borderRadius={16} />
        ) : (
          <Actions asset={asset} />
        )}
      </div>

      <Activity activity={activity} loading={loading} />
    </Subpage>
  );
}

export default TokenDetailPage;
